import { useState, useEffect, useRef } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Buildings, Plus, PencilSimple, Trash, MagnifyingGlass, ArrowUp, ArrowDown, ArrowsDownUp, Export, ClockCounterClockwise, UploadSimple, DownloadSimple, FileArrowUp, CheckCircle, Warning, X } from "@phosphor-icons/react";
import { useTableFeatures, exportCSV } from "@/hooks/useTableFeatures";
import { useNavigate } from "react-router-dom";
import { useBulkSelect } from "@/hooks/useBulkSelect";
import BulkActionBar from "@/components/BulkActionBar";
import { Checkbox } from "@/components/ui/checkbox";

const defaultForm = {
  name: "", entity_type: "school", ben: "", address: "", city: "", state: "",
  zip_code: "", contact_name: "", contact_email: "", contact_phone: "",
  urban_rural: "urban", discount_rate: 0, enrollment: 0, nslp: 0, c2_budget_estimate: 0, notes: "",
  cipa_date: "",
};

const entityTypes = ["school", "library", "consortium", "nia"];
const states = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC"];

export default function Clients() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importStep, setImportStep] = useState("upload"); // upload | preview | result
  const [importPreview, setImportPreview] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();
  const bulk = useBulkSelect();
  const bulkComplete = () => { bulk.clear(); load(); };
  const [form, setForm] = useState({ ...defaultForm });

  const { search, setSearch, sortKey, sortDir, toggleSort, filters, setFilter, clearFilters, processed, activeFilterCount } = useTableFeatures(items);
  const csvCols = [
    { label: "Name", key: "name" }, { label: "Type", key: "entity_type" }, { label: "BEN", key: "ben" },
    { label: "Contact", key: "contact_name" }, { label: "City", key: "city" }, { label: "State", key: "state" },
    { label: "Discount Rate", key: "discount_rate" }, { label: "Email", key: "contact_email" }, { label: "Phone", key: "contact_phone" },
    { label: "CIPA Date", key: "cipa_date" },
  ];
  const SH = ({ k, children, className = "" }) => (
    <TableHead className={`py-3 px-4 text-xs uppercase tracking-wider text-[#6B7280] font-semibold cursor-pointer select-none hover:text-[#1A1D20] transition-colors ${className}`} onClick={() => toggleSort(k)}>
      <div className="flex items-center gap-1">{children}{sortKey === k ? (sortDir === "asc" ? <ArrowUp size={10} /> : <ArrowDown size={10} />) : <ArrowsDownUp size={10} className="opacity-30" />}</div>
    </TableHead>
  );

  useEffect(() => { load(); }, []);

  const load = async () => {
    try { const { data } = await api.get("/clients"); setItems(data); }
    catch { toast.error("Failed to load clients"); }
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    try {
      if (editing) { await api.put(`/clients/${editing}`, form); toast.success("Client updated"); }
      else { await api.post("/clients", form); toast.success("Client created"); }
      setOpen(false); load();
    } catch { toast.error("Failed to save"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this client?")) return;
    try { await api.delete(`/clients/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed to delete"); }
  };

  const updateCipa = async (item, date) => {
    setItems((prev) => prev.map((x) => x.id === item.id ? { ...x, cipa_date: date } : x));
    try {
      await api.put(`/clients/${item.id}`, { ...item, cipa_date: date });
    } catch {
      toast.error("Failed to save CIPA date");
      load();
    }
  };

  const edit = (item) => { setForm({ ...item }); setEditing(item.id); setOpen(true); };
  const openNew = () => { setForm({ ...defaultForm }); setEditing(null); setOpen(true); };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const downloadTemplate = async () => {
    try {
      const response = await api.get("/clients/template", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "client_import_template.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Template downloaded");
    } catch {
      toast.error("Failed to download template");
    }
  };

  const openImport = () => {
    setImportOpen(true);
    setImportStep("upload");
    setImportPreview(null);
    setImportResult(null);
    setSelectedFile(null);
    setImporting(false);
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSelectedFile(file);

    const formData = new FormData();
    formData.append("file", file);
    try {
      const { data } = await api.post("/clients/import-preview", formData);
      setImportPreview(data);
      setImportStep("preview");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to read file");
      setSelectedFile(null);
    }
  };

  const executeImport = async () => {
    if (!selectedFile) return;
    setImporting(true);
    const formData = new FormData();
    formData.append("file", selectedFile);
    try {
      const { data } = await api.post("/clients/import", formData);
      setImportResult(data);
      setImportStep("result");
      if (data.imported > 0) {
        toast.success(`${data.imported} client${data.imported > 1 ? "s" : ""} imported`);
        load();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="p-6" data-testid="clients-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#6B7280] mb-1">Management</p>
          <h1 className="text-2xl sm:text-3xl tracking-tight font-semibold text-[#1A1D20]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Clients</h1>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="enrollment-data-btn" onClick={() => navigate("/clients/enrollment")} className="bg-transparent text-[#1A1D20] border border-[#1A1D20] hover:bg-[#F8F9FA] rounded-none font-medium px-4 py-2 text-sm flex items-center gap-2 transition-colors">
            Enrollment Data
          </button>
          <button data-testid="import-clients-btn" onClick={openImport} className="bg-transparent text-[#1A1D20] border border-[#1A1D20] hover:bg-[#F8F9FA] rounded-none font-medium px-4 py-2 text-sm flex items-center gap-2 transition-colors">
            <UploadSimple size={16} weight="bold" /> Import
          </button>
          <button data-testid="add-client-btn" onClick={openNew} className="bg-[#002FA7] text-white hover:bg-[#002480] rounded-none font-medium px-4 py-2 text-sm flex items-center gap-2 transition-colors">
            <Plus size={16} weight="bold" /> Add Client
          </button>
        </div>
      </div>

      <div className="mb-4 flex items-center gap-3 flex-wrap">
        <div className="relative max-w-sm flex-1 min-w-[200px]">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" />
          <Input data-testid="search-clients" placeholder="Search clients..." value={search} onChange={(e) => setSearch(e.target.value)} className="rounded-none pl-9" />
        </div>
        <Select value={filters.entity_type || "all"} onValueChange={(v) => setFilter("entity_type", v)}>
          <SelectTrigger data-testid="filter-type" className="rounded-none w-[140px]"><SelectValue placeholder="All Types" /></SelectTrigger>
          <SelectContent className="rounded-none"><SelectItem value="all">All Types</SelectItem>{entityTypes.map((t) => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filters.urban_rural || "all"} onValueChange={(v) => setFilter("urban_rural", v)}>
          <SelectTrigger data-testid="filter-urban" className="rounded-none w-[130px]"><SelectValue placeholder="All Areas" /></SelectTrigger>
          <SelectContent className="rounded-none"><SelectItem value="all">All Areas</SelectItem><SelectItem value="urban">Urban</SelectItem><SelectItem value="rural">Rural</SelectItem></SelectContent>
        </Select>
        {activeFilterCount > 0 && <button onClick={clearFilters} className="text-xs text-[#002FA7] hover:underline">Clear filters</button>}
        <button data-testid="export-clients-btn" onClick={() => exportCSV(processed, csvCols, "clients.csv")} className="ml-auto flex items-center gap-1.5 bg-transparent text-[#1A1D20] border border-[#1A1D20] hover:bg-[#F8F9FA] rounded-none font-medium px-3 py-2 text-xs transition-colors">
          <Export size={14} /> Export CSV
        </button>
      </div>

      <div className="border border-gray-200 bg-white">
        <Table>
          <TableHeader>
            <TableRow className="data-table-header">
              <TableHead className="py-3 px-3 w-[40px]"><Checkbox data-testid="select-all-clients" checked={bulk.selected.size === processed.length && processed.length > 0} onCheckedChange={() => bulk.toggleAll(processed.map(i => i.id))} /></TableHead>
              <SH k="name">Name</SH>
              <SH k="entity_type">Type</SH>
              <SH k="ben">BEN</SH>
              <SH k="contact_name">Contact</SH>
              <SH k="contact_email">Email</SH>
              <SH k="contact_phone">Phone</SH>
              <SH k="discount_rate">Discount</SH>
              <SH k="cipa_date">CIPA Date</SH>
              <TableHead className="py-3 px-4 text-xs uppercase tracking-wider text-[#6B7280] font-semibold w-[100px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {processed.length === 0 ? (
              <TableRow><TableCell colSpan={10} className="text-center py-8 text-[#6B7280] text-sm">No clients found</TableCell></TableRow>
            ) : processed.map((item) => (
              <TableRow key={item.id} className="border-b border-gray-100 hover:bg-gray-50">
                <TableCell className="py-3 px-3"><Checkbox data-testid={`select-client-${item.id}`} checked={bulk.selected.has(item.id)} onCheckedChange={() => bulk.toggle(item.id)} /></TableCell>
                <TableCell className="py-3 px-4 text-[#1A1D20] font-medium">{item.name}</TableCell>
                <TableCell className="py-3 px-4"><Badge variant="outline" className="rounded-none capitalize text-xs">{item.entity_type}</Badge></TableCell>
                <TableCell className="py-3 px-4 text-[#1A1D20] font-mono text-xs">{item.ben || "—"}</TableCell>
                <TableCell className="py-3 px-4 text-sm text-[#1A1D20]">{item.contact_name || "—"}</TableCell>
                <TableCell className="py-3 px-4 text-sm text-[#6B7280]">{item.contact_email ? <a href={`mailto:${item.contact_email}`} className="text-[#002FA7] hover:underline">{item.contact_email}</a> : "—"}</TableCell>
                <TableCell className="py-3 px-4 text-sm text-[#6B7280]">{item.contact_phone ? <a href={`tel:${item.contact_phone}`} className="hover:text-[#002FA7]">{item.contact_phone}</a> : "—"}</TableCell>
                <TableCell className="py-3 px-4 text-sm font-medium text-[#002FA7]">{item.discount_rate > 0 ? `${item.discount_rate}%` : "—"}</TableCell>
                <TableCell className="py-3 px-4">
                  <Input
                    data-testid={`client-cipa-${item.id}`}
                    type="date"
                    value={item.cipa_date || ""}
                    onChange={(e) => updateCipa(item, e.target.value)}
                    className="rounded-none h-8 text-xs px-2 w-[140px]"
                  />
                </TableCell>
                <TableCell className="py-3 px-4">
                  <div className="flex items-center gap-1">
                    <button data-testid={`timeline-client-${item.id}`} onClick={() => navigate(`/clients/${item.id}/timeline`)} className="p-1.5 hover:bg-blue-50 transition-colors" title="View Timeline"><ClockCounterClockwise size={14} className="text-[#002FA7]" /></button>
                    <button data-testid={`edit-client-${item.id}`} onClick={() => edit(item)} className="p-1.5 hover:bg-gray-100 transition-colors"><PencilSimple size={14} className="text-[#6B7280]" /></button>
                    <button data-testid={`delete-client-${item.id}`} onClick={() => remove(item.id)} className="p-1.5 hover:bg-red-50 transition-colors"><Trash size={14} className="text-[#FF2A00]" /></button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <BulkActionBar count={bulk.count} collection="clients" selectedIds={bulk.selected} onComplete={bulkComplete} />

      {/* Add/Edit Client Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none sm:max-w-[640px] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{editing ? "Edit" : "Add"} Client</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Name *</label>
                <Input data-testid="client-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} className="rounded-none" />
              </div>
              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Entity Type</label>
                <Select value={form.entity_type} onValueChange={(v) => set("entity_type", v)}>
                  <SelectTrigger data-testid="client-type-select" className="rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-none">{entityTypes.map((t) => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">BEN</label>
                <Input data-testid="client-ben-input" value={form.ben} onChange={(e) => set("ben", e.target.value)} className="rounded-none" placeholder="Billed Entity Number" />
              </div>
              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Urban/Rural</label>
                <Select value={form.urban_rural} onValueChange={(v) => set("urban_rural", v)}>
                  <SelectTrigger data-testid="client-urban-select" className="rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-none">
                    <SelectItem value="urban">Urban</SelectItem>
                    <SelectItem value="rural">Rural</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Address</label>
              <Input data-testid="client-address-input" value={form.address} onChange={(e) => set("address", e.target.value)} className="rounded-none" />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">City</label>
                <Input data-testid="client-city-input" value={form.city} onChange={(e) => set("city", e.target.value)} className="rounded-none" />
              </div>
              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">State</label>
                <Select value={form.state} onValueChange={(v) => set("state", v)}>
                  <SelectTrigger data-testid="client-state-select" className="rounded-none"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent className="rounded-none max-h-[200px]">{states.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Zip</label>
                <Input data-testid="client-zip-input" value={form.zip_code} onChange={(e) => set("zip_code", e.target.value)} className="rounded-none" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Contact Name</label>
                <Input data-testid="client-contact-input" value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} className="rounded-none" />
              </div>
              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Email</label>
                <Input data-testid="client-email-input" value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)} className="rounded-none" />
              </div>
              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Phone</label>
                <Input data-testid="client-phone-input" value={form.contact_phone} onChange={(e) => set("contact_phone", e.target.value)} className="rounded-none" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Discount Rate (%)</label>
                <Input data-testid="client-discount-input" type="number" value={form.discount_rate} onChange={(e) => set("discount_rate", parseFloat(e.target.value) || 0)} className="rounded-none" />
              </div>
              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Estimated C2 Budget ($)</label>
                <Input data-testid="client-c2budget-input" type="number" value={form.c2_budget_estimate} onChange={(e) => set("c2_budget_estimate", parseFloat(e.target.value) || 0)} className="rounded-none" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Enrollment</label>
                <Input data-testid="client-enrollment-input" type="number" value={form.enrollment} onChange={(e) => set("enrollment", parseInt(e.target.value) || 0)} className="rounded-none" placeholder="Student count" />
              </div>
              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">NSLP (%)</label>
                <Input data-testid="client-nslp-input" type="number" value={form.nslp} onChange={(e) => set("nslp", parseFloat(e.target.value) || 0)} className="rounded-none" placeholder="National School Lunch Program %" />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Notes</label>
              <textarea data-testid="client-notes-input" value={form.notes} onChange={(e) => set("notes", e.target.value)} className="w-full border border-gray-300 rounded-none px-3 py-2 text-sm focus:outline-none focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] min-h-[60px]" />
            </div>
          </div>
          <DialogFooter>
            <button data-testid="cancel-client-btn" onClick={() => setOpen(false)} className="bg-transparent text-[#1A1D20] border border-[#1A1D20] hover:bg-[#F8F9FA] rounded-none font-medium px-4 py-2 text-sm transition-colors">Cancel</button>
            <button data-testid="save-client-btn" onClick={save} className="bg-[#002FA7] text-white hover:bg-[#002480] rounded-none font-medium px-4 py-2 text-sm transition-colors">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Clients Dialog */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="rounded-none sm:max-w-[680px] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              <div className="flex items-center gap-2">
                <FileArrowUp size={20} className="text-[#002FA7]" />
                Import Clients
              </div>
            </DialogTitle>
          </DialogHeader>

          {importStep === "upload" && (
            <div className="py-4">
              <div className="mb-6 p-4 bg-[#F8F9FA] border border-gray-200">
                <p className="text-sm text-[#1A1D20] mb-3">Upload a CSV or Excel file with client data. Need the right format?</p>
                <button data-testid="download-template-btn" onClick={downloadTemplate} className="flex items-center gap-2 text-sm font-medium text-[#002FA7] hover:text-[#002480] transition-colors">
                  <DownloadSimple size={16} weight="bold" />
                  Download CSV Template
                </button>
              </div>

              <input ref={fileInputRef} type="file" accept=".csv,.xlsx,.xls" onChange={handleFileSelect} className="hidden" />

              <button
                data-testid="select-file-btn"
                onClick={() => fileInputRef.current?.click()}
                className="w-full border-2 border-dashed border-gray-300 hover:border-[#002FA7] p-8 text-center transition-colors group"
              >
                <UploadSimple size={32} className="mx-auto text-[#6B7280] group-hover:text-[#002FA7] mb-3 transition-colors" />
                <p className="text-sm font-medium text-[#1A1D20] mb-1">Click to select a file</p>
                <p className="text-xs text-[#6B7280]">Supports CSV, XLSX, XLS</p>
              </button>

              <div className="mt-6">
                <p className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-2">Expected Columns</p>
                <div className="flex flex-wrap gap-1.5">
                  {["name*", "entity_type", "ben", "address", "city", "state", "zip_code", "contact_name", "contact_email", "contact_phone", "urban_rural", "discount_rate", "enrollment", "nslp", "c2_budget_estimate"].map((col) => (
                    <span key={col} className={`inline-block px-2 py-0.5 text-xs border ${col.endsWith("*") ? "border-[#002FA7] text-[#002FA7] font-semibold" : "border-gray-200 text-[#6B7280]"}`}>
                      {col}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {importStep === "preview" && importPreview && (
            <div className="py-4">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-sm font-medium text-[#1A1D20]">{selectedFile?.name}</p>
                  <p className="text-xs text-[#6B7280]">{importPreview.total_rows} rows found</p>
                </div>
                <button onClick={() => { setImportStep("upload"); setSelectedFile(null); setImportPreview(null); }} className="text-xs text-[#6B7280] hover:text-[#1A1D20]">
                  Change file
                </button>
              </div>

              <div className="mb-4 flex gap-4">
                <div className="flex-1">
                  <p className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1.5">Matched Columns</p>
                  <div className="flex flex-wrap gap-1">
                    {importPreview.matched_columns.map((col) => (
                      <span key={col} className="inline-flex items-center gap-1 px-2 py-0.5 text-xs border border-green-200 text-green-700 bg-green-50">
                        <CheckCircle size={10} /> {col}
                      </span>
                    ))}
                    {importPreview.matched_columns.length === 0 && <span className="text-xs text-[#6B7280]">None</span>}
                  </div>
                </div>
                {importPreview.unmatched_columns.length > 0 && (
                  <div className="flex-1">
                    <p className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1.5">Unmatched (ignored)</p>
                    <div className="flex flex-wrap gap-1">
                      {importPreview.unmatched_columns.map((col) => (
                        <span key={col} className="inline-flex items-center gap-1 px-2 py-0.5 text-xs border border-amber-200 text-amber-700 bg-amber-50">
                          <Warning size={10} /> {col}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="border border-gray-200 overflow-x-auto mb-4">
                <p className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider px-3 py-2 bg-[#F8F9FA] border-b border-gray-200">Preview (first 5 rows)</p>
                <Table>
                  <TableHeader>
                    <TableRow>
                      {importPreview.columns.slice(0, 8).map((col) => (
                        <TableHead key={col} className="py-2 px-3 text-xs uppercase tracking-wider text-[#6B7280] font-semibold whitespace-nowrap">{col}</TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {importPreview.preview.map((row, i) => (
                      <TableRow key={i} className="border-b border-gray-50">
                        {importPreview.columns.slice(0, 8).map((col) => (
                          <TableCell key={col} className="py-2 px-3 text-xs text-[#1A1D20] max-w-[150px] truncate">{String(row[col] ?? "")}</TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}

          {importStep === "result" && importResult && (
            <div className="py-4">
              <div className={`p-4 mb-4 border ${importResult.imported > 0 ? "border-green-200 bg-green-50" : "border-red-200 bg-red-50"}`}>
                <div className="flex items-center gap-2 mb-2">
                  {importResult.imported > 0 ? <CheckCircle size={20} className="text-green-600" /> : <Warning size={20} className="text-red-600" />}
                  <span className="text-sm font-semibold text-[#1A1D20]">Import Complete</span>
                </div>
                <div className="grid grid-cols-3 gap-4 text-center mt-3">
                  <div>
                    <p className="text-2xl font-bold text-green-600">{importResult.imported}</p>
                    <p className="text-xs text-[#6B7280]">Imported</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-amber-600">{importResult.skipped}</p>
                    <p className="text-xs text-[#6B7280]">Skipped</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-[#1A1D20]">{importResult.total_rows}</p>
                    <p className="text-xs text-[#6B7280]">Total Rows</p>
                  </div>
                </div>
              </div>
              {importResult.errors?.length > 0 && (
                <div className="p-3 border border-amber-200 bg-amber-50">
                  <p className="text-xs font-semibold text-amber-700 uppercase tracking-wider mb-1">Errors</p>
                  {importResult.errors.map((err, i) => (
                    <p key={i} className="text-xs text-amber-800">{err}</p>
                  ))}
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            {importStep === "upload" && (
              <button onClick={() => setImportOpen(false)} className="bg-transparent text-[#1A1D20] border border-[#1A1D20] hover:bg-[#F8F9FA] rounded-none font-medium px-4 py-2 text-sm transition-colors">Close</button>
            )}
            {importStep === "preview" && (
              <>
                <button onClick={() => { setImportStep("upload"); setSelectedFile(null); setImportPreview(null); }} className="bg-transparent text-[#1A1D20] border border-[#1A1D20] hover:bg-[#F8F9FA] rounded-none font-medium px-4 py-2 text-sm transition-colors">Back</button>
                <button data-testid="confirm-import-btn" onClick={executeImport} disabled={importing} className="bg-[#002FA7] text-white hover:bg-[#002480] disabled:opacity-50 rounded-none font-medium px-4 py-2 text-sm transition-colors flex items-center gap-2">
                  {importing ? "Importing..." : `Import ${importPreview.total_rows} Clients`}
                </button>
              </>
            )}
            {importStep === "result" && (
              <button data-testid="close-import-btn" onClick={() => setImportOpen(false)} className="bg-[#002FA7] text-white hover:bg-[#002480] rounded-none font-medium px-4 py-2 text-sm transition-colors">Done</button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
